import React, { useEffect, useState } from 'react';
import { Box, Chip, FormControl, MenuItem, Select, SelectChangeEvent, InputLabel, OutlinedInput, Checkbox, ListItemText, TextField, InputAdornment, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import EntityViewDrawer, { EntitySection } from './ui/EntityViewDrawer';
import ConfirmDialog from './ui/ConfirmDialog';
import { useToast } from './ui/ToastProvider';
import { VALID_SUB_AGENTS, type ValidSubAgent } from '../utils/constants';
interface SubAgent {
  id: string;
  name: string;
  description: string;
  headOfficeAddress: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  settlementAccount: string;
  status: 'active' | 'inactive';
}
interface SubAgentsTableProps {
  onEditSubAgent: (subAgent: SubAgent) => void;
  onManageUsers: (subAgentId: string) => void;
  searchQuery: string;
}
const BRANCH_CODES = ['GH-ACC-001', 'GH-KUM-001', 'NG-LAG-001', 'NG-ABJ-001', 'SN-DKR-001', 'UG-KLA-001', 'KE-NBI-001', 'CI-ABJ-001'];
// Detailed sub-agent data
const ECOBANK_SUB_AGENT_DATA = [
// IMTO Agents
{
  name: 'Paga Ghana',
  desc: 'IMTO agent — leading digital payments provider in Ghana',
  address: '14 Independence Ave, Accra, Ghana',
  contact: 'Kwesi Amponsah',
  phone: '+233 20 456 7890',
  email: 'kwesi@pagagh.com',
  account: 'ECO-IMTO-GH-001'
}, {
  name: 'MoneyGram Nigeria',
  desc: 'IMTO agent — international money transfer operator in Lagos',
  address: '25 Marina Road, Lagos Island, Nigeria',
  contact: 'Chinedu Okoro',
  phone: '+234 81 234 5678',
  email: 'chinedu@moneygram.ng',
  account: 'ECO-IMTO-NG-001'
}, {
  name: 'WorldRemit Kenya',
  desc: 'IMTO agent — cross-border remittance provider in Nairobi',
  address: '8 Kenyatta Ave, Nairobi, Kenya',
  contact: 'Grace Mwangi',
  phone: '+254 722 345 678',
  email: 'grace@worldremit.ke',
  account: 'ECO-IMTO-KE-001'
},
// Fintech Agents
{
  name: 'Flutterwave Ghana',
  desc: 'Fintech agent — payment technology company in Accra',
  address: '3 Oxford Street, Osu, Accra, Ghana',
  contact: 'Ama Boateng',
  phone: '+233 24 567 8901',
  email: 'ama@flutterwave.com.gh',
  account: 'ECO-FIN-GH-001'
}, {
  name: 'Flutterwave Nigeria',
  desc: 'Fintech agent — payment infrastructure provider in Lagos',
  address: '42 Saka Tinubu, Victoria Island, Lagos, Nigeria',
  contact: 'Tunde Adeyemi',
  phone: '+234 803 456 7890',
  email: 'tunde@flutterwave.com.ng',
  account: 'ECO-FIN-NG-001'
}, {
  name: 'M-Pesa Kenya',
  desc: 'Fintech agent — mobile money operator via Safaricom in Nairobi',
  address: 'Safaricom House, Waiyaki Way, Nairobi, Kenya',
  contact: 'Peter Kipchoge',
  phone: '+254 728 567 890',
  email: 'peter@safaricom.co.ke',
  account: 'ECO-FIN-KE-001'
}, {
  name: 'Orange Money Senegal',
  desc: 'Fintech agent — mobile money service in Dakar',
  address: '12 Rue Amadou Assane Ndoye, Dakar, Senegal',
  contact: 'Moussa Diop',
  phone: '+221 77 890 1234',
  email: 'moussa@orange.sn',
  account: 'ECO-FIN-SN-001'
},
// Cash Agents
{
  name: 'Accra Money Center',
  desc: 'Cash agent — retail cash pickup point in Accra',
  address: '7 Kwame Nkrumah Circle, Accra, Ghana',
  contact: 'Kwesi Mensah',
  phone: '+233 24 678 9012',
  email: 'kwesi@accramoney.com.gh',
  account: 'ECO-CASH-GH-001'
}, {
  name: 'Lagos Express',
  desc: 'Cash agent — retail cash pickup in Lagos metropolitan',
  address: '15 Allen Avenue, Ikeja, Lagos, Nigeria',
  contact: 'Bola Adebayo',
  phone: '+234 805 678 9012',
  email: 'bola@lagosexpress.com.ng',
  account: 'ECO-CASH-NG-001'
}, {
  name: 'Nairobi Remit Center',
  desc: 'Cash agent — retail remittance center in Nairobi',
  address: '22 Moi Avenue, Nairobi, Kenya',
  contact: 'David Mwangi',
  phone: '+254 722 789 012',
  email: 'david@nairobiremit.co.ke',
  account: 'ECO-CASH-KE-001'
}];
// Mock data
const MOCK_SUB_AGENTS: SubAgent[] = Array(20).fill(null).map((_, index) => {
  const agentData = ECOBANK_SUB_AGENT_DATA[index % ECOBANK_SUB_AGENT_DATA.length];
  return {
    id: index < 3 ? `AGT-IMTO-${['GH', 'NG', 'KE'][index]}-${String(Math.floor(index / 3) + 1).padStart(3, '0')}` : index < 7 ? `AGT-FIN-${['GH', 'NG', 'KE', 'SN'][index - 3]}-${String(Math.floor((index - 3) / 4) + 1).padStart(3, '0')}` : index < 10 ? `AGT-CASH-${['GH', 'NG', 'KE'][index - 7]}-${String(Math.floor((index - 7) / 3) + 1).padStart(3, '0')}` : `AGT-${['IMTO', 'FIN', 'CASH'][index % 3]}-${['GH', 'NG', 'KE', 'SN', 'UG'][index % 5]}-${String(Math.floor(index / 5) + 1).padStart(3, '0')}`,
    name: agentData.name,
    description: agentData.desc,
    headOfficeAddress: agentData.address,
    contactPerson: agentData.contact,
    contactPhone: agentData.phone,
    contactEmail: agentData.email,
    settlementAccount: agentData.account + (index >= ECOBANK_SUB_AGENT_DATA.length ? `-${index}` : ''),
    status: index % 7 === 0 ? 'inactive' : 'active'
  };
});
const SubAgentsTable: React.FC<SubAgentsTableProps> = ({
  onEditSubAgent,
  onManageUsers,
  searchQuery
}) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    branchCodes: [] as string[],
    status: 'all',
    search: searchQuery || '',
    name: '',
    description: '',
    headOfficeAddress: '',
    contactPerson: '',
    contactPhone: '',
    contactEmail: '',
    settlementAccount: ''
  });
  const [loading, setLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState<{
    [key: string]: string | string[];
  }>({});
  // State for view drawer
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [selectedSubAgent, setSelectedSubAgent] = useState<SubAgent | null>(null);
  const [isLoadingSubAgent, setIsLoadingSubAgent] = useState(false);
  const [subAgentError, setSubAgentError] = useState<string | undefined>();
  // State for status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | undefined>();
  // Get toast context
  const {
    showToast
  } = useToast();
  // Handle column filter change
  const handleColumnFilterChange = (columnId: string, value: string | string[]) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: value
    }));
  };
  // Handle text filter change
  const handleTextFilterChange = (field: string, value: string) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };
  // Update search when searchQuery prop changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: searchQuery || ''
    }));
  }, [searchQuery]);
  // Handle view sub agent
  const handleViewSubAgent = async (subAgent: SubAgent) => {
    setSelectedSubAgent(subAgent);
    setViewDrawerOpen(true);
    setIsLoadingSubAgent(true);
    setSubAgentError(undefined);
    try {
      // Simulate API call to fetch sub agent details
      await new Promise(resolve => setTimeout(resolve, 1200));
      // In a real app, you would fetch detailed sub agent data here
      setIsLoadingSubAgent(false);
      showToast(`Sub agent details loaded`, 'success');
    } catch (error) {
      setSubAgentError('Failed to load sub agent details. Please try again.');
      setIsLoadingSubAgent(false);
    }
  };
  // Handle status change
  const handleStatusChange = (subAgent: SubAgent) => {
    setSelectedSubAgent(subAgent);
    setStatusDialogOpen(true);
    setStatusError(undefined);
  };
  // Confirm status change
  const confirmStatusChange = async (reason?: string) => {
    if (!selectedSubAgent) return;
    setIsChangingStatus(true);
    setStatusError(undefined);
    try {
      // Simulate API call to change status
      await new Promise(resolve => setTimeout(resolve, 1500));
      // In a real app, you would call the API to update status
      // Update local state to reflect the change
      const newStatus = selectedSubAgent.status === 'active' ? 'inactive' : 'active';
      setSelectedSubAgent({
        ...selectedSubAgent,
        status: newStatus
      });
      // Close dialog and show toast
      setStatusDialogOpen(false);
      setIsChangingStatus(false);
      showToast(`Sub agent ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`, 'success', undefined, 5000);
      // In a real app, you would refetch the data or update it in state
    } catch (error) {
      setStatusError('Failed to update status. Please try again.');
      setIsChangingStatus(false);
    }
  };
  // Handle manage users
  const handleManageUsers = (subAgent: SubAgent) => {
    navigate(`/sub-agent-users?subAgent=${subAgent.id}`);
    showToast(`Navigated to users for ${subAgent.name}`, 'info');
  };
  // Prepare sections for view drawer
  const getSubAgentSections = (): EntitySection[] => {
    if (!selectedSubAgent) return [];
    return [{
      title: 'Overview',
      fields: [{
        label: 'Sub Agent ID',
        value: selectedSubAgent.id
      }, {
        label: 'Status',
        value: selectedSubAgent.status === 'active' ? 'Active' : 'Inactive',
        type: 'chip',
        chipColor: selectedSubAgent.status === 'active' ? '#10B981' : '#EF4444',
        chipBgColor: selectedSubAgent.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'
      }, {
        label: 'Description',
        value: selectedSubAgent.description || 'N/A'
      }]
    }, {
      title: 'Contact Information',
      fields: [{
        label: 'Contact Person',
        value: selectedSubAgent.contactPerson
      }, {
        label: 'Contact Phone',
        value: selectedSubAgent.contactPhone,
        type: 'phone'
      }, {
        label: 'Email',
        value: selectedSubAgent.contactEmail,
        type: 'email'
      }, {
        label: 'Head Office Address',
        value: selectedSubAgent.headOfficeAddress
      }]
    }, {
      title: 'Account Details',
      fields: [{
        label: 'Settlement Account',
        value: selectedSubAgent.settlementAccount
      }]
    }];
  };
  // Apply filters to the data
  const filteredSubAgents = MOCK_SUB_AGENTS.filter(subAgent => {
    // Apply search query
    if (filters.search) {
      const query = filters.search.toLowerCase();
      if (!subAgent.name.toLowerCase().includes(query) && !subAgent.contactPerson.toLowerCase().includes(query) && !subAgent.contactPhone.toLowerCase().includes(query) && !subAgent.contactEmail.toLowerCase().includes(query) && !subAgent.description.toLowerCase().includes(query)) {
        return false;
      }
    }
    // Column-specific text filters
    if (filters.name && !subAgent.name.toLowerCase().includes(filters.name.toLowerCase())) {
      return false;
    }
    if (filters.description && !subAgent.description.toLowerCase().includes(filters.description.toLowerCase())) {
      return false;
    }
    if (filters.headOfficeAddress && !subAgent.headOfficeAddress.toLowerCase().includes(filters.headOfficeAddress.toLowerCase())) {
      return false;
    }
    if (filters.contactPerson && !subAgent.contactPerson.toLowerCase().includes(filters.contactPerson.toLowerCase())) {
      return false;
    }
    if (filters.contactPhone && !subAgent.contactPhone.toLowerCase().includes(filters.contactPhone.toLowerCase())) {
      return false;
    }
    if (filters.contactEmail && !subAgent.contactEmail.toLowerCase().includes(filters.contactEmail.toLowerCase())) {
      return false;
    }
    if (filters.settlementAccount && !subAgent.settlementAccount.toLowerCase().includes(filters.settlementAccount.toLowerCase())) {
      return false;
    }
    // Branch code filter
    if (filters.branchCodes.length > 0 && !filters.branchCodes.some(code => subAgent.headOfficeAddress.toLowerCase().includes(code.toLowerCase()))) {
      return false;
    }
    // Status filter
    if (filters.status !== 'all' && subAgent.status !== filters.status) {
      return false;
    }
    // Status filter from column filter
    if (columnFilters.status && columnFilters.status !== 'all' && subAgent.status !== columnFilters.status) {
      return false;
    }
    return true;
  });
  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      branchCodes: [],
      status: 'all',
      search: '',
      name: '',
      description: '',
      headOfficeAddress: '',
      contactPerson: '',
      contactPhone: '',
      contactEmail: '',
      settlementAccount: ''
    });
    setColumnFilters({});
  };
  // Handle search change
  const handleSearchChange = (value: string) => {
    setFilters({
      ...filters,
      search: value
    });
  };
  // Define filter chips
  const filterChips: FilterChip[] = [...(filters.name ? [{
    id: 'name',
    label: 'Name',
    value: filters.name,
    onDelete: () => handleTextFilterChange('name', '')
  }] : []), ...(filters.description ? [{
    id: 'description',
    label: 'Description',
    value: filters.description,
    onDelete: () => handleTextFilterChange('description', '')
  }] : []), ...(filters.headOfficeAddress ? [{
    id: 'headOfficeAddress',
    label: 'Head Office Address',
    value: filters.headOfficeAddress,
    onDelete: () => handleTextFilterChange('headOfficeAddress', '')
  }] : []), ...(filters.contactPerson ? [{
    id: 'contactPerson',
    label: 'Contact Person',
    value: filters.contactPerson,
    onDelete: () => handleTextFilterChange('contactPerson', '')
  }] : []), ...(filters.contactPhone ? [{
    id: 'contactPhone',
    label: 'Contact Phone',
    value: filters.contactPhone,
    onDelete: () => handleTextFilterChange('contactPhone', '')
  }] : []), ...(filters.contactEmail ? [{
    id: 'contactEmail',
    label: 'Contact Email',
    value: filters.contactEmail,
    onDelete: () => handleTextFilterChange('contactEmail', '')
  }] : []), ...(filters.settlementAccount ? [{
    id: 'settlementAccount',
    label: 'Settlement Account',
    value: filters.settlementAccount,
    onDelete: () => handleTextFilterChange('settlementAccount', '')
  }] : []), ...filters.branchCodes.map(code => ({
    id: `branchCode-${code}`,
    label: 'Branch Code',
    value: code,
    onDelete: () => setFilters({
      ...filters,
      branchCodes: filters.branchCodes.filter(c => c !== code)
    })
  })), ...(filters.status !== 'all' ? [{
    id: 'status',
    label: 'Status',
    value: filters.status,
    onDelete: () => setFilters({
      ...filters,
      status: 'all'
    })
  }] : []), ...Object.entries(columnFilters).filter(([key, value]) => value !== 'all' && value !== '').map(([key, value]) => ({
    id: `column-${key}-${value}`,
    label: columns.find(col => col.id === key)?.label || key,
    value: typeof value === 'string' ? value : value.join(', '),
    onDelete: () => handleColumnFilterChange(key, key === 'status' ? 'all' : '')
  }))];
  // Define columns
  const columns: Column<SubAgent>[] = [{
    id: 'name',
    label: 'Name',
    renderFilter: () => <TextField size="small" placeholder="Search name" value={filters.name} onChange={e => handleTextFilterChange('name', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2006">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2007" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2005" />
  }, {
    id: 'description',
    label: 'Description',
    renderFilter: () => <TextField size="small" placeholder="Search description" value={filters.description} onChange={e => handleTextFilterChange('description', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2009">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2010" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2008" />
  }, {
    id: 'headOfficeAddress',
    label: 'Head Office Address',
    renderFilter: () => <TextField size="small" placeholder="Search address" value={filters.headOfficeAddress} onChange={e => handleTextFilterChange('headOfficeAddress', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2012">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2013" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2011" />
  }, {
    id: 'contactPerson',
    label: 'Contact Person',
    renderFilter: () => <TextField size="small" placeholder="Search contact" value={filters.contactPerson} onChange={e => handleTextFilterChange('contactPerson', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2015">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2016" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2014" />
  }, {
    id: 'contactPhone',
    label: 'Contact Phone',
    renderFilter: () => <TextField size="small" placeholder="Search phone" value={filters.contactPhone} onChange={e => handleTextFilterChange('contactPhone', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2018">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2019" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2017" />
  }, {
    id: 'contactEmail',
    label: 'Contact Email',
    renderFilter: () => <TextField size="small" placeholder="Search email" value={filters.contactEmail} onChange={e => handleTextFilterChange('contactEmail', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2021">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2022" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2020" />
  }, {
    id: 'settlementAccount',
    label: 'Settlement Account (ECO)',
    renderFilter: () => <TextField size="small" placeholder="Search account" value={filters.settlementAccount} onChange={e => handleTextFilterChange('settlementAccount', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-2024">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-2025" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-2023" />
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status === 'active' ? 'Active' : 'Inactive'} size="small" sx={{
      backgroundColor: row.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: row.status === 'active' ? '#10B981' : '#EF4444',
      height: 24
    }} data-id="element-2026" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-2027">
          <Select value={columnFilters.status || 'all'} onChange={e => handleColumnFilterChange('status', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-2028">
            <MenuItem value="all" data-id="element-2029">All Status</MenuItem>
            <MenuItem value="active" data-id="element-2030">Active</MenuItem>
            <MenuItem value="inactive" data-id="element-2031">Inactive</MenuItem>
          </Select>
        </FormControl>
  }];
  // Define row actions
  const rowActions: RowAction<SubAgent>[] = [{
    icon: 'lucide:eye',
    label: 'View',
    onClick: handleViewSubAgent
  }, {
    icon: 'lucide:edit',
    label: 'Edit',
    onClick: onEditSubAgent
  }, {
    icon: 'lucide:x-circle',
    label: 'Deactivate',
    onClick: handleStatusChange,
    condition: subAgent => subAgent.status === 'active'
  }, {
    icon: 'lucide:check-circle',
    label: 'Activate',
    onClick: handleStatusChange,
    condition: subAgent => subAgent.status === 'inactive'
  }, {
    icon: 'lucide:users',
    label: 'Manage Users',
    onClick: handleManageUsers
  }, {
    icon: 'lucide:history',
    label: 'Audit Log',
    onClick: subAgent => {
      console.log('View audit trail for sub agent', subAgent);
      showToast('Audit log feature coming soon', 'info');
    }
  }];
  // Handle export
  const handleExport = () => {
    console.log('Export data');
    showToast('Data export initiated', 'success');
  };
  return <>
      <DataTable title="Sub Agents" data={filteredSubAgents} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={handleClearFilters} loading={loading} initialVisibleColumns={['name', 'description', 'contactPerson', 'contactPhone', 'contactEmail', 'status']} onSearchChange={handleSearchChange} searchPlaceholder="Search sub agents by name, contact person, email..." searchValue={filters.search} onExport={handleExport} emptyStateMessage="No sub agents found" data-id="element-2032" />
      {/* View Drawer */}
      {selectedSubAgent && <EntityViewDrawer open={viewDrawerOpen} onClose={() => setViewDrawerOpen(false)} onEdit={() => {
      setViewDrawerOpen(false);
      onEditSubAgent(selectedSubAgent);
    }} title={selectedSubAgent.name} subtitle="Sub Agent Details" entityId={selectedSubAgent.id} sections={getSubAgentSections()} isLoading={isLoadingSubAgent} error={subAgentError} onRetry={() => handleViewSubAgent(selectedSubAgent)} data-id="element-2033" />}
      {/* Status Change Dialog */}
      {selectedSubAgent && <ConfirmDialog open={statusDialogOpen} title={`${selectedSubAgent.status === 'active' ? 'Deactivate' : 'Activate'} Sub Agent`} message={`Are you sure you want to ${selectedSubAgent.status === 'active' ? 'deactivate' : 'activate'} ${selectedSubAgent.name}?`} confirmLabel={selectedSubAgent.status === 'active' ? 'Deactivate' : 'Activate'} showReason={true} reasonLabel="Reason (optional)" reasonPlaceholder={`Enter reason for ${selectedSubAgent.status === 'active' ? 'deactivation' : 'activation'}...`} onConfirm={confirmStatusChange} onCancel={() => setStatusDialogOpen(false)} isLoading={isChangingStatus} error={statusError} data-id="element-2034" />}
    </>;
};
export default SubAgentsTable;